import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  BillPayment,
  CreateBillPaymentData,
  BillPaymentsResponse,
  BillPaymentResponse,
  BillPaymentsListParams,
  ApplyBillPaymentAllocationsData,
  ReplaceBillPaymentAllocationsData,
  UpdateBillPaymentData,
  VoidBillPaymentData,
} from '../types/billPayment';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

const buildBillPaymentsUrl = (params?: BillPaymentsListParams) => {
  const query = new URLSearchParams();
  if (params?.vendor_id) {
    query.set('vendor_id', String(params.vendor_id));
  }
  if (params?.unapplied !== undefined) {
    query.set('unapplied', String(params.unapplied));
  }
  const qs = query.toString();
  return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILL_PAYMENTS.GET_ALL}${qs ? `?${qs}` : ''}`;
};

export const getBillPayments = async (
  params?: BillPaymentsListParams
): Promise<BillPayment[]> => {
  const response = await fetch(buildBillPaymentsUrl(params), {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch bill payments');
  }

  const result: BillPaymentsResponse = await response.json();
  return result.data ?? [];
};

export const getAllBillPayments = async (): Promise<BillPayment[]> => getBillPayments();

export const getBillPaymentById = async (id: number): Promise<BillPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILL_PAYMENTS.GET_BY_ID}/${id}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch bill payment');
  }

  const result: BillPaymentResponse = await response.json();
  return result.data;
};

export const updateBillPayment = async (
  paymentId: number,
  data: UpdateBillPaymentData
): Promise<BillPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILL_PAYMENTS.GET_BY_ID}/${paymentId}`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update bill payment');
  }

  const result: BillPaymentResponse = await response.json();
  return result.data;
};

export const applyBillPaymentAllocations = async (
  paymentId: number,
  data: ApplyBillPaymentAllocationsData
): Promise<BillPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILL_PAYMENTS.GET_BY_ID}/${paymentId}/allocations`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to apply unapplied prepayment');
  }

  const result: BillPaymentResponse = await response.json();
  return result.data;
};

export const replaceBillPaymentAllocations = async (
  paymentId: number,
  data: ReplaceBillPaymentAllocationsData
): Promise<BillPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILL_PAYMENTS.GET_BY_ID}/${paymentId}/allocations`,
    {
      method: 'PUT',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update bill payment allocations');
  }

  const result: BillPaymentResponse = await response.json();
  return result.data;
};

export const createBillPayment = async (data: CreateBillPaymentData): Promise<BillPayment> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILL_PAYMENTS.CREATE}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to record bill payment');
  }

  const result: BillPaymentResponse = await response.json();
  return result.data;
};

export const voidBillPayment = async (
  id: number,
  data: VoidBillPaymentData = {}
): Promise<BillPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILL_PAYMENTS.VOID}/${id}/void`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        void_reason: data.void_reason?.trim() || null,
      }),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to void bill payment');
  }

  const result: BillPaymentResponse = await response.json();
  return result.data;
};
